/**
 * Component standards, per category. These are captured from phase 1 because
 * the phase 4 compatibility checker ("will this fit my bike?") depends on
 * them. Option values are the names riders and manufacturers actually use.
 *
 * Lengths are millimetres. Keys are stable identifiers stored in
 * component.specs: add new fields or options freely, but never rename a key
 * or an option value that has been stored.
 */
import { z } from 'zod'
import type { ComponentCategory } from './enums'

export type SpecField =
  | {
      key: string
      label: string
      kind: 'select'
      options: readonly string[]
      hint?: string
      /** Also accept a value not in `options` ("Other"), for non-standard sizes. */
      other?: boolean
    }
  | {
      key: string
      label: string
      kind: 'number'
      unit?: 'mm' | 'g' | 'teeth' | 'speeds' | 'ml' | 'deg'
      min?: number
      max?: number
      hint?: string
    }
  | { key: string; label: string; kind: 'text'; hint?: string }
  | { key: string; label: string; kind: 'boolean'; hint?: string }

const WHEEL_SIZES = ['29"', '27.5"', '26"', '24"', '20"', '700c', '650b'] as const
const BB_SHELLS = [
  'BSA 73mm',
  'BSA 68mm',
  'BSA 83mm',
  'T47 68mm',
  'T47 85.5mm',
  'PF92 / BB92',
  'BB86',
  'PF30 68mm',
  'PF30 73mm',
  'BB30',
  'BB386 EVO',
  'Italian 70mm',
] as const
const REAR_AXLES = [
  'Boost 148x12',
  '142x12',
  'SuperBoost 157x12',
  'DH 157x12',
  '135x12',
  '135mm QR',
  '130mm QR',
  '135x10 bolt-on',
  'Singlespeed 120mm',
] as const
const FRONT_AXLES = ['Boost 15x110', '15x100', '20x110', '12x100', '9mm QR', '12x100 road'] as const
const FREEHUBS = [
  'Shimano HG',
  'Shimano Micro Spline',
  'SRAM XD',
  'SRAM XDR',
  'Campagnolo N3W',
  'Campagnolo',
  'Singlespeed',
] as const
const ROTOR_MOUNTS = ['6-bolt', 'Center Lock'] as const
const BRAKE_MOUNTS = [
  'Post mount 160mm',
  'Post mount 180mm',
  'Post mount 200mm',
  'Post mount 140mm',
  'Flat mount 140mm',
  'Flat mount 160mm',
  'IS mount',
  'Rim brake',
] as const
const STEERERS = [
  'Tapered 1-1/8" to 1.5"',
  'Straight 1-1/8"',
  'Straight 1.5"',
  '1" threaded',
] as const
const SPEEDS = { kind: 'number', unit: 'speeds', min: 1, max: 13 } as const
const SEATPOST_DIAMETERS = [
  '27.2mm',
  '30.9mm',
  '31.6mm',
  '34.9mm',
  '30.0mm',
  '28.6mm',
  'Aero / proprietary',
] as const
const CLAMP_DIAMETERS = ['31.8mm', '35mm', '25.4mm', '26.0mm'] as const
const CRANK_SPINDLES = [
  'SRAM DUB 28.99mm',
  'Shimano Hollowtech II 24mm',
  '30mm',
  'BB30 30mm',
  'Square taper',
  'Octalink',
  'ISIS',
  'GXP 24/22mm',
] as const
const WHEEL_POSITION_HINT = 'Front or rear is set when the part is fitted to a bike.'

export const specFields: Record<ComponentCategory, readonly SpecField[]> = {
  frame: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    { key: 'bb_shell', label: 'Bottom bracket shell', kind: 'select', options: BB_SHELLS },
    { key: 'rear_axle', label: 'Rear axle', kind: 'select', options: REAR_AXLES },
    {
      key: 'headset',
      label: 'Headset standard',
      kind: 'text',
      hint: 'S.H.I.S., for example ZS44/28.6 | EC44/40',
    },
    {
      key: 'seatpost_diameter',
      label: 'Seatpost diameter',
      kind: 'select',
      options: SEATPOST_DIAMETERS,
    },
    { key: 'rear_brake_mount', label: 'Rear brake mount', kind: 'select', options: BRAKE_MOUNTS },
    {
      key: 'max_rotor_rear',
      label: 'Largest rear rotor',
      kind: 'number',
      unit: 'mm',
      min: 120,
      max: 230,
    },
    {
      key: 'derailleur_hanger',
      label: 'Derailleur hanger',
      kind: 'text',
      hint: 'For example SRAM UDH, or the hanger part number',
    },
    { key: 'max_tyre_width', label: 'Largest tyre', kind: 'number', unit: 'mm', min: 15, max: 130 },
    {
      key: 'rear_travel',
      label: 'Rear travel',
      kind: 'number',
      unit: 'mm',
      min: 0,
      max: 250,
      hint: '0 for a hardtail',
    },
  ],
  fork: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    { key: 'travel', label: 'Travel', kind: 'number', unit: 'mm', min: 0, max: 220 },
    { key: 'axle', label: 'Axle', kind: 'select', options: FRONT_AXLES },
    { key: 'steerer', label: 'Steerer', kind: 'select', options: STEERERS },
    { key: 'brake_mount', label: 'Brake mount', kind: 'select', options: BRAKE_MOUNTS },
    { key: 'offset', label: 'Offset', kind: 'number', unit: 'mm', min: 20, max: 60 },
    { key: 'spring', label: 'Spring', kind: 'select', options: ['Air', 'Coil', 'Rigid'] },
    { key: 'max_rotor', label: 'Largest rotor', kind: 'number', unit: 'mm', min: 140, max: 230 },
  ],
  shock: [
    { key: 'eye_to_eye', label: 'Eye to eye', kind: 'number', unit: 'mm', min: 130, max: 260 },
    { key: 'stroke', label: 'Stroke', kind: 'number', unit: 'mm', min: 20, max: 90 },
    {
      key: 'mount',
      label: 'Mount',
      kind: 'select',
      options: ['Standard eyelet', 'Trunnion', 'Metric eyelet'],
    },
    { key: 'spring', label: 'Spring', kind: 'select', options: ['Air', 'Coil'] },
    {
      key: 'hardware',
      label: 'Mounting hardware',
      kind: 'text',
      hint: 'For example 8x30mm top, 8x20mm bottom',
    },
  ],
  wheel: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    {
      key: 'axle',
      label: 'Axle',
      kind: 'select',
      options: [...FRONT_AXLES, ...REAR_AXLES],
      hint: WHEEL_POSITION_HINT,
    },
    {
      key: 'freehub',
      label: 'Freehub',
      kind: 'select',
      options: FREEHUBS,
      hint: 'Rear wheels only',
    },
    { key: 'rotor_mount', label: 'Rotor mount', kind: 'select', options: ROTOR_MOUNTS },
    {
      key: 'internal_width',
      label: 'Rim internal width',
      kind: 'number',
      unit: 'mm',
      min: 13,
      max: 50,
    },
    { key: 'tubeless_ready', label: 'Tubeless ready', kind: 'boolean' },
  ],
  hub: [
    { key: 'axle', label: 'Axle', kind: 'select', options: [...FRONT_AXLES, ...REAR_AXLES] },
    { key: 'freehub', label: 'Freehub', kind: 'select', options: FREEHUBS },
    { key: 'rotor_mount', label: 'Rotor mount', kind: 'select', options: ROTOR_MOUNTS },
    { key: 'spoke_holes', label: 'Spoke holes', kind: 'number', min: 12, max: 48 },
  ],
  rim: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    {
      key: 'internal_width',
      label: 'Internal width',
      kind: 'number',
      unit: 'mm',
      min: 13,
      max: 50,
    },
    { key: 'spoke_holes', label: 'Spoke holes', kind: 'number', min: 12, max: 48 },
    { key: 'tubeless_ready', label: 'Tubeless ready', kind: 'boolean' },
  ],
  tyre: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    {
      key: 'width',
      label: 'Width',
      kind: 'number',
      unit: 'mm',
      min: 18,
      max: 130,
      hint: 'A 2.4in tyre is about 61mm',
    },
    { key: 'etrto', label: 'ETRTO', kind: 'text', hint: 'For example 61-622' },
    {
      key: 'casing',
      label: 'Casing',
      kind: 'text',
      hint: 'For example EXO+, DoubleDown, Super Gravity',
    },
    { key: 'compound', label: 'Compound', kind: 'text', hint: 'For example MaxxGrip, T9' },
    {
      key: 'setup',
      label: 'Set-up',
      kind: 'select',
      options: ['Tubeless', 'Tube', 'Tubeless with insert', 'Tubular'],
    },
  ],
  inner_tube: [
    { key: 'wheel_size', label: 'Wheel size', kind: 'select', options: WHEEL_SIZES, other: true },
    { key: 'width_range', label: 'Width range', kind: 'text', hint: 'For example 2.3-2.6in' },
    { key: 'valve', label: 'Valve', kind: 'select', options: ['Presta', 'Schrader', 'Dunlop'] },
  ],
  chain: [
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
    { key: 'links', label: 'Links', kind: 'number', min: 90, max: 130 },
    {
      key: 'connector',
      label: 'Connector',
      kind: 'text',
      hint: 'For example SRAM PowerLock, Shimano quick link',
    },
  ],
  cassette: [
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
    {
      key: 'range',
      label: 'Range',
      kind: 'text',
      hint: 'Smallest to largest cog, for example 10-52',
    },
    { key: 'freehub', label: 'Freehub', kind: 'select', options: FREEHUBS },
  ],
  chainring: [
    { key: 'teeth', label: 'Teeth', kind: 'number', unit: 'teeth', min: 20, max: 60 },
    {
      key: 'mount',
      label: 'Mount',
      kind: 'select',
      options: [
        'SRAM 3-bolt direct mount',
        'SRAM 8-bolt direct mount',
        'Shimano 12-speed direct mount',
        '104 BCD',
        '96 BCD',
        '110 BCD',
        '130 BCD',
        'Other',
      ],
    },
    { key: 'offset', label: 'Offset', kind: 'number', unit: 'mm', min: -10, max: 10 },
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
  ],
  crankset: [
    { key: 'spindle', label: 'Spindle', kind: 'select', options: CRANK_SPINDLES },
    { key: 'length', label: 'Crank length', kind: 'number', unit: 'mm', min: 110, max: 185 },
    { key: 'chainline', label: 'Chainline', kind: 'number', unit: 'mm', min: 40, max: 60 },
  ],
  bottom_bracket: [
    { key: 'shell', label: 'Shell', kind: 'select', options: BB_SHELLS },
    { key: 'spindle', label: 'Spindle', kind: 'select', options: CRANK_SPINDLES },
  ],
  derailleur_rear: [
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
    { key: 'max_cog', label: 'Largest cog', kind: 'number', unit: 'teeth', min: 21, max: 55 },
    {
      key: 'mount',
      label: 'Mount',
      kind: 'select',
      options: ['Standard hanger', 'SRAM UDH direct mount', 'Shimano direct mount'],
    },
    {
      key: 'actuation',
      label: 'Shifting',
      kind: 'select',
      options: ['Cable', 'Wireless (SRAM AXS)', 'Wireless (Shimano Di2)', 'Wired Di2'],
    },
  ],
  derailleur_front: [
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
    {
      key: 'mount',
      label: 'Mount',
      kind: 'select',
      options: [
        'Braze-on',
        'Clamp 28.6mm',
        'Clamp 31.8mm',
        'Clamp 34.9mm',
        'Direct mount',
        'E-type',
      ],
    },
  ],
  shifter: [
    { key: 'speeds', label: 'Speeds', ...SPEEDS },
    {
      key: 'mount',
      label: 'Mount',
      kind: 'select',
      options: ['Handlebar clamp', 'Shimano I-Spec EV', 'SRAM MatchMaker X', 'Road brake lever'],
    },
    {
      key: 'actuation',
      label: 'Shifting',
      kind: 'select',
      options: ['Cable', 'Wireless (SRAM AXS)', 'Wireless (Shimano Di2)', 'Wired Di2'],
    },
  ],
  brake: [
    {
      key: 'type',
      label: 'Type',
      kind: 'select',
      options: ['Hydraulic disc', 'Mechanical disc', 'Rim'],
    },
    {
      key: 'fluid',
      label: 'Fluid',
      kind: 'select',
      options: ['Mineral oil', 'DOT 5.1', 'DOT 4', 'None (cable)'],
    },
    {
      key: 'caliper_mount',
      label: 'Caliper mount',
      kind: 'select',
      options: ['Post mount', 'Flat mount', 'Rim'],
    },
    { key: 'pistons', label: 'Pistons', kind: 'number', min: 1, max: 6 },
    {
      key: 'pad_shape',
      label: 'Pad shape',
      kind: 'text',
      hint: 'For example Shimano D03S, SRAM Code',
    },
  ],
  brake_pads: [
    {
      key: 'pad_shape',
      label: 'Pad shape',
      kind: 'text',
      hint: 'For example Shimano D03S, SRAM Code',
    },
    {
      key: 'compound',
      label: 'Compound',
      kind: 'select',
      options: ['Organic (resin)', 'Sintered (metal)', 'Semi-metallic'],
    },
  ],
  brake_rotor: [
    { key: 'diameter', label: 'Diameter', kind: 'number', unit: 'mm', min: 140, max: 230 },
    { key: 'mount', label: 'Mount', kind: 'select', options: ROTOR_MOUNTS },
    {
      key: 'thickness',
      label: 'Thickness when new',
      kind: 'number',
      unit: 'mm',
      min: 1.5,
      max: 2.5,
      hint: 'Replace when worn to the minimum stamped on the rotor',
    },
    {
      key: 'min_thickness',
      label: 'Minimum thickness',
      kind: 'number',
      unit: 'mm',
      min: 1,
      max: 2.5,
    },
  ],
  headset: [
    {
      key: 'standard',
      label: 'Standard (S.H.I.S.)',
      kind: 'text',
      hint: 'For example ZS44/28.6 | EC44/40',
    },
    { key: 'steerer', label: 'Steerer', kind: 'select', options: STEERERS },
  ],
  stem: [
    { key: 'bar_clamp', label: 'Bar clamp', kind: 'select', options: CLAMP_DIAMETERS },
    { key: 'length', label: 'Length', kind: 'number', unit: 'mm', min: 20, max: 150 },
    {
      key: 'steerer_clamp',
      label: 'Steerer clamp',
      kind: 'select',
      options: ['1-1/8" (28.6mm)', '1.5"', '1" (25.4mm)'],
    },
    { key: 'rise', label: 'Rise', kind: 'number', unit: 'deg', min: -30, max: 45 },
  ],
  handlebar: [
    { key: 'clamp', label: 'Clamp diameter', kind: 'select', options: CLAMP_DIAMETERS },
    { key: 'width', label: 'Width', kind: 'number', unit: 'mm', min: 350, max: 850 },
    { key: 'rise', label: 'Rise', kind: 'number', unit: 'mm', min: 0, max: 100 },
    {
      key: 'material',
      label: 'Material',
      kind: 'select',
      options: ['Aluminium', 'Carbon', 'Steel', 'Titanium'],
    },
  ],
  grips: [{ key: 'lock_on', label: 'Lock-on', kind: 'boolean' }],
  seatpost: [
    { key: 'diameter', label: 'Diameter', kind: 'select', options: SEATPOST_DIAMETERS },
    { key: 'type', label: 'Type', kind: 'select', options: ['Rigid', 'Dropper'] },
    { key: 'travel', label: 'Dropper travel', kind: 'number', unit: 'mm', min: 0, max: 260 },
    {
      key: 'routing',
      label: 'Dropper routing',
      kind: 'select',
      options: ['Internal', 'External', 'Wireless'],
    },
  ],
  saddle: [
    {
      key: 'rails',
      label: 'Rails',
      kind: 'select',
      options: ['Round 7mm', 'Oval 7x9mm', 'Oval 7x10mm', 'Other'],
    },
  ],
  pedals: [
    {
      key: 'type',
      label: 'Type',
      kind: 'select',
      options: [
        'Flat',
        'Clipless SPD',
        'Clipless SPD-SL',
        'Clipless Look',
        'Clipless Crankbrothers',
        'Clipless Time',
        'Hybrid',
      ],
    },
    { key: 'thread', label: 'Thread', kind: 'select', options: ['9/16"', '1/2"'] },
  ],
  cables: [
    {
      key: 'kind',
      label: 'What',
      kind: 'select',
      options: ['Gear inner and outer', 'Brake inner and outer', 'Dropper cable', 'Hydraulic hose'],
    },
  ],
  sealant: [
    { key: 'volume', label: 'Volume per tyre', kind: 'number', unit: 'ml', min: 10, max: 250 },
  ],
  other: [],
}

function fieldSchema(f: SpecField): z.ZodType {
  switch (f.kind) {
    case 'select':
      // "Other": any short text, so a non-standard size can still be recorded.
      return f.other
        ? z.string().trim().min(1).max(40)
        : z.enum(f.options as unknown as [string, ...string[]])
    case 'number': {
      let n = z.number()
      if (f.min !== undefined) n = n.min(f.min)
      if (f.max !== undefined) n = n.max(f.max)
      return n
    }
    case 'text':
      return z.string().trim().min(1).max(120)
    case 'boolean':
      return z.boolean()
  }
}

const cache = new Map<ComponentCategory, z.ZodType<Record<string, unknown>>>()

/**
 * The zod schema for a category's specs. Every field is optional; unknown
 * keys are rejected so typos never end up in the stored standards.
 */
export function specsSchema(category: ComponentCategory): z.ZodType<Record<string, unknown>> {
  let schema = cache.get(category)
  if (!schema) {
    const shape = Object.fromEntries(
      specFields[category].map((f) => [f.key, fieldSchema(f).optional()]),
    )
    schema = z.strictObject(shape) as unknown as z.ZodType<Record<string, unknown>>
    cache.set(category, schema)
  }
  return schema
}

/** Human-readable value for display, e.g. `160 mm`, `Yes`. */
export function formatSpecValue(field: SpecField, value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  if (field.kind === 'boolean') return value ? 'Yes' : 'No'
  if (field.kind === 'number' && field.unit) {
    const unit = { mm: 'mm', g: 'g', teeth: 't', speeds: '-speed', ml: 'ml', deg: '°' }[field.unit]
    return field.unit === 'speeds'
      ? `${String(value)}${unit}`
      : `${String(value)}${unit === '°' ? '' : ' '}${unit}`
  }
  return String(value)
}
